import { useState } from 'react'
import { SectionCard, Button, Checkbox, TextInput, Select } from '@datahub/design-system'
import { tabsForCycle } from '@datahub/shared'
import type { PortalContext, TabProgress, ValidationIssue } from '../lib/types'

interface ReviewTabProps {
  context: PortalContext
  tabProgress: Record<string, TabProgress>
  issues: ValidationIssue[]
  submitting: boolean
  onEditTab: (tab: string) => void
  onSubmit: (input: { submittedByName: string; submittedByEmail: string; submittedByContactId: string | null }) => void
}

/** The declaration names the kind of entity the contact is answering for. */
function declarationFor(form: PortalContext['form']): string {
  const noun = form === 'gp' ? 'this firm' : form === 'lp' ? 'this institution' : 'this company'
  return `I confirm that I am authorised to share this information on behalf of ${noun}, that it is accurate to the best of my knowledge, and that Stears may use it in line with its data policy. Items marked confidential will not be published.`
}

/** Tab 9 (FRM-13). */
export function ReviewTab({ context, tabProgress, issues, submitting, onEditTab, onSubmit }: ReviewTabProps) {
  const [contactId, setContactId] = useState<string>(context.contacts[0]?.id ?? '')
  const [name, setName] = useState(context.contacts[0]?.fullName ?? '')
  const [email, setEmail] = useState(context.contacts[0]?.email ?? '')
  const [accepted, setAccepted] = useState(false)

  const errors = issues.filter((i) => i.severity === 'error')
  const warnings = issues.filter((i) => i.severity === 'warning')
  const canSubmit = accepted && errors.length === 0 && name.trim() !== '' && /.+@.+\..+/.test(email) && !submitting

  const contactOptions = [
    ...context.contacts.map((c) => ({ value: c.id, label: `${c.fullName} (${c.email})` })),
    { value: 'other', label: 'Someone else' },
  ]

  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      <SectionCard title="Your update at a glance">
        <div className="flex flex-col">
          {tabsForCycle(context.form, context.cycle?.requestedTabs ?? []).filter((t) => t.key !== 'review').map((tab) => {
            const progress = tabProgress[tab.key]
            const tabErrors = errors.filter((i) => i.tab === tab.key).length
            return (
              <div
                key={tab.key}
                className="flex items-center"
                style={{ padding: '10px 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}
              >
                <span className="font-mulish text-white" style={{ fontSize: 12, width: '42%' }}>{tab.label}</span>
                <span className="font-mulish" style={{ fontSize: 12, flex: 1, color: tabErrors > 0 ? '#F05042' : '#9CA3AF' }}>
                  {tabErrors > 0
                    ? `${tabErrors} answer${tabErrors === 1 ? '' : 's'} need attention`
                    : `${progress?.pct ?? 0}% complete${progress?.skipped ? ' · nothing to report' : ''}`}
                </span>
                <button
                  type="button"
                  onClick={() => onEditTab(tab.key)}
                  className="text-text-hyperlink hover:underline"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
                >
                  Edit
                </button>
              </div>
            )
          })}
        </div>
      </SectionCard>

      {(errors.length > 0 || warnings.length > 0) && (
        <SectionCard title={errors.length > 0 ? 'Before you can submit' : 'Worth a look'}>
          <div className="flex flex-col" style={{ padding: '12px 20px', gap: 8 }}>
            {[...errors, ...warnings].slice(0, 40).map((issue, i) => (
              <button
                key={`${issue.tab}-${issue.field}-${i}`}
                type="button"
                onClick={() => onEditTab(issue.tab)}
                className="text-left flex"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', gap: 8 }}
              >
                <span style={{ color: issue.severity === 'error' ? '#F05042' : '#9CA3AF', fontSize: 12 }}>
                  {issue.severity === 'error' ? '•' : '◦'}
                </span>
                <span className="font-mulish text-white hover:underline" style={{ fontSize: 12, lineHeight: '16px' }}>
                  {issue.message}
                </span>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Submit">
        <div className="flex flex-col" style={{ padding: '16px 20px', gap: 20, maxWidth: 520 }}>
          <Select
            label="Submitted by"
            size="large"
            options={contactOptions}
            value={contactId}
            onChange={(v) => {
              setContactId(v)
              const contact = context.contacts.find((c) => c.id === v)
              if (contact) { setName(contact.fullName); setEmail(contact.email) }
              else { setName(''); setEmail('') }
            }}
          />
          {contactId === 'other' && (
            <>
              <TextInput label="Your name" size="large" value={name} onChange={setName} />
              <TextInput label="Your email" size="large" inputType="email" value={email} onChange={setEmail} />
            </>
          )}
          <Checkbox checked={accepted} onChange={setAccepted} label={declarationFor(context.form)} />
          <div>
            <Button
              variant="filled-grey"
              size="medium"
              disabled={!canSubmit}
              loading={submitting}
              onClick={() => onSubmit({ submittedByName: name, submittedByEmail: email, submittedByContactId: contactId === 'other' ? null : contactId })}
            >
              Submit {context.cycle?.periodLabel ?? ''} update
            </Button>
          </div>
        </div>
      </SectionCard>
    </div>
  )
}
